import { useEffect, useState } from 'react'
import { View } from 'react-native'
import Button from '@ca/ui/buttons/Button'
import ModalWide from '@ca/ui/components/Modal/variants/ModalWide'
import Segmented from '@ca/ui/elements/Segmented'
import Typography from '@ca/ui/typography'
import type { OpticalMeasureSettings } from '../index'
import {
  applyShTestingSettings,
  DEFAULT_SH_TESTING_SETTINGS,
  isDefaultShTestingSettings,
  isShTestingSettings11,
  resolveShTestingSettings,
  SH_TESTING_SETTINGS_1_1,
} from './shTestingSettings'
import type { ShTestingSettingsSelection } from './types'

type Props = {
  open: boolean
  settings: OpticalMeasureSettings
  close: () => void
  onSave: (
    selection: ShTestingSettingsSelection,
    settings: OpticalMeasureSettings
  ) => void
  onReset: (settings: OpticalMeasureSettings) => void
}

/**
 * Each option carries its own description, and the row shows the selected
 * one's: the numbers are what a tester needs, and a static row blurb cannot
 * carry three sets of them. They mirror the preset tables in
 * `shTestingSettings.ts` — keep them in step when a preset is retuned.
 *
 * Keep each one to a single line (roughly 80 characters). Swapping options
 * reflows the whole modal otherwise, and it has to stay on one screen.
 */
type SettingOption<T extends string> = {
  id: T
  label: string
  description: string
}

// There is no Final Frame row here. The frozen rerun's model is still its own
// setting (`sh.coreml.finalFrameModelVariant`, small_512 vs medium_512) and the
// dev settings screen exposes it; it is left out of this modal because it is not
// one of the axes the SH testing passes vary. See SH_MODEL_RESOLUTION.md.

// Owns the plan's residual knobs — the ones no other row exposes — and is
// applied before every other row, so a row selection always wins over it. See
// `applyShTestingSettings`.
const BASELINE_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['baseline']>
> = [
  {
    id: '1.1',
    label: 'Baseline 1.1',
    description:
      'Frozen 1.1: final frame counted double, range spread gate, pose gate ±8°.',
  },
  {
    id: '1.2',
    label: 'Baseline 1.2',
    description:
      'Shipped: final frame counted once, MAD spread gate, pose gate ±3°.',
  },
]

const DISTANCE_TOLERANCE_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['distanceTolerance']>
> = [
  {
    id: 'current',
    label: 'Legacy 1.1',
    description:
      'Old wide band: prompts past ±15 cm, rejects past ±20 cm — never prompted.',
  },
  {
    id: 'tight',
    label: 'Tight',
    description:
      'Middle band: prompts past ±10 cm, rejects past ±14 — still lands short.',
  },
  {
    id: 'tightest',
    label: 'Tightest',
    description:
      'Shipped: 50 cm (35 with VBOX), prompts past ±6 and rejects at ±7.',
  },
]

// Plain wording on purpose. The mechanism is vertex parallax and the code says
// so — `parallaxCorrectionMm`, `lensDepthMm` — but a settings row is read by
// people who need to know what it changes, not how it works.
const PITCH_CORRECTION_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['pitchCorrection']>
> = [
  {
    id: 'off',
    label: 'Off',
    description: 'Shipped: head tilt is limited by the gate but not corrected.',
  },
  {
    id: 'on',
    label: 'On',
    description:
      'Corrects for head tilt: lenses sit ahead of the eyes, so tilting shifts it.',
  },
]

const PITCH_REFERENCE_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['pitchReference']>
> = [
  {
    id: 'neutral',
    label: 'ARKit Neutral',
    description:
      "Legacy 1.1 — ARKit's zero, which lets the gate reach 8° chin-down.",
  },
  {
    id: 'look-up-3',
    label: 'Look Up · 3°',
    description:
      'Shipped: gate centred on the pose subjects hold at 35 cm (−6°…0°).',
  },
  {
    id: 'look-up-6',
    label: 'Look Up · 6°',
    description: 'Zero moves 6° up for the strongest chin-up pose.',
  },
]

const SCALE_REFERENCE_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['scaleReference']>
> = [
  {
    id: 'vbox-first',
    label: 'VBOX First',
    description:
      'Shipped: full VBOX trust to 35% disagreement with PD, pure PD at 60%.',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    description: 'Hands over sooner: full VBOX trust to 20%, pure PD at 35%.',
  },
  {
    id: 'pd-guarded',
    label: 'PD Guarded',
    description: 'PD as guard rail: full VBOX trust to 8%, pure PD at 20%.',
  },
]

const CAPTURE_SAMPLES_OPTIONS: Array<
  SettingOption<ShTestingSettingsSelection['captureSamples']>
> = [
  {
    id: '5',
    label: 'Quick · 5',
    description: 'Five eligible frames — quickest hold, the 1.1 sample target.',
  },
  {
    id: '9',
    label: 'Standard · 9',
    description: 'Nine eligible frames: shipped, ~1.34x tighter than five.',
  },
  {
    id: '13',
    label: 'Thorough · 13',
    description: 'Thirteen eligible frames: steadiest median, longest hold.',
  },
]

function SettingRow<T extends string>({
  title,
  options,
  value,
  onChange,
}: {
  title: string
  options: Array<SettingOption<T>>
  value: T
  onChange: (id: T) => void
}) {
  const selected = options.find((option) => option.id === value)

  return (
    <View className="gap-2">
      <View className="gap-0.5">
        <Typography variant="MDB">{title}</Typography>
        <Typography variant="XS" className="text-black/50">
          {selected?.description}
        </Typography>
      </View>
      <Segmented
        options={options}
        value={selected}
        onChange={(option) => onChange(option.id)}
      />
    </View>
  )
}

export function ShTestingSettingsModal({
  open,
  settings,
  close,
  onSave,
  onReset,
}: Props) {
  const [selection, setSelection] = useState(() =>
    resolveShTestingSettings(settings)
  )

  useEffect(() => {
    if (!open) return
    setSelection(resolveShTestingSettings(settings))
  }, [open, settings])

  const nextSettings = applyShTestingSettings(settings, selection)
  const hasChanges = JSON.stringify(nextSettings) !== JSON.stringify(settings)
  const hasNonDefaultSelection = !isDefaultShTestingSettings(selection)

  return (
    <ModalWide
      open={open}
      close={close}
      title="Measurement Settings"
      showClose
      hideBottomSpace
      body={
        // The rows have to clear the fold on an 11" iPad, so the vertical
        // rhythm is deliberately tighter than the shared modal defaults. Back
        // to six: Framing and Frame Aggregation were dropped to make room for
        // Pitch Correction.
        //
        // Those two were *removed*, not hidden. A row that still applies but is
        // not rendered is a value the tester cannot see or reset, silently
        // outranking every shipped default — which is exactly how a stored
        // `tight` chip cost us four rounds of measurement.
        //
        // Head Pitch Zero was itself slated for removal under the plan's C1,
        // back when head pitch looked like a 0.04 mm/deg rounding error. It
        // measures 0.38, so it stays.
        <View className="gap-5 px-16 pb-2">
          <SettingRow
            title="Baseline"
            options={BASELINE_OPTIONS}
            value={selection.baseline}
            onChange={(baseline) =>
              setSelection((current) => ({ ...current, baseline }))
            }
          />

          <SettingRow
            title="Distance Tolerance"
            options={DISTANCE_TOLERANCE_OPTIONS}
            value={selection.distanceTolerance}
            onChange={(distanceTolerance) =>
              setSelection((current) => ({ ...current, distanceTolerance }))
            }
          />

          <SettingRow
            title="Head Tilt Correction"
            options={PITCH_CORRECTION_OPTIONS}
            value={selection.pitchCorrection}
            onChange={(pitchCorrection) =>
              setSelection((current) => ({ ...current, pitchCorrection }))
            }
          />

          <SettingRow
            title="Head Pitch Zero"
            options={PITCH_REFERENCE_OPTIONS}
            value={selection.pitchReference}
            onChange={(pitchReference) =>
              setSelection((current) => ({ ...current, pitchReference }))
            }
          />

          <SettingRow
            title="Scale Reference"
            options={SCALE_REFERENCE_OPTIONS}
            value={selection.scaleReference}
            onChange={(scaleReference) =>
              setSelection((current) => ({ ...current, scaleReference }))
            }
          />

          <SettingRow
            title="Capture Samples"
            options={CAPTURE_SAMPLES_OPTIONS}
            value={selection.captureSamples}
            onChange={(captureSamples) =>
              setSelection((current) => ({ ...current, captureSamples }))
            }
          />
        </View>
      }
      buttons={
        <>
          <Button
            label="Reset Defaults"
            variant="outlined"
            disabled={!hasNonDefaultSelection}
            onPress={() =>
              onReset(
                applyShTestingSettings(settings, DEFAULT_SH_TESTING_SETTINGS)
              )
            }
          />
          {/*
            Stages the selection rather than saving it, the same as tapping the
            seven rows by hand would: the tester still confirms with Save and
            Restart. Reset Defaults keeps restoring the *new* defaults — this is
            the separate control for the old ones.
          */}
          <Button
            label="Load 1.1"
            variant="outlined"
            disabled={isShTestingSettings11(selection)}
            onPress={() => setSelection(SH_TESTING_SETTINGS_1_1)}
          />
          <Button
            label="Save and Restart"
            disabled={!hasChanges}
            onPress={() => onSave(selection, nextSettings)}
          />
        </>
      }
    />
  )
}
